import { Component } from '@theme/component';

/**
 * Desktop menu for the announcement bar.
 *
 * Enhances the native `<details>` dropdowns: keeps a single dropdown open at
 * a time, closes on outside click or Escape, and keeps `aria-expanded` in
 * sync on the summary elements.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement[]} [dropdowns]
 *
 * @extends {Component<Refs>}
 */
export class NaAnnouncementMenu extends Component {
    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();

        const { signal } = this.#abortController;

        document.addEventListener('click', this.#onDocumentClick, { signal });
        document.addEventListener('keydown', this.#onKeyDown, { signal });
        this.addEventListener('toggle', this.#onToggle, { signal, capture: true });

        if (window.matchMedia('(min-width: 750px)').matches) {
            for (const dropdown of this.refs.dropdowns ?? []) {
                dropdown.addEventListener('pointerenter', this.#onPointerEnter, { signal });
                dropdown.addEventListener('pointerleave', this.#onPointerLeave, { signal });
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    /** @param {Event} event */
    #onToggle = (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement)) return;

        const summary = details.querySelector('summary');
        summary?.setAttribute('aria-expanded', `${details.open}`);

        if (!details.open || !this.refs.dropdowns) return;

        for (const dropdown of this.refs.dropdowns) {
            if (dropdown !== details && !dropdown.contains(details)) {
                dropdown.removeAttribute('open');
            }
        }
    };

    /** @param {PointerEvent} event */
    #onPointerEnter = (event) => {
        const details = event.currentTarget;
        if (!(details instanceof HTMLDetailsElement)) return;
        details.setAttribute('open', '');
    };

    /** @param {PointerEvent} event */
    #onPointerLeave = (event) => {
        const details = event.currentTarget;
        if (!(details instanceof HTMLDetailsElement)) return;
        details.removeAttribute('open');
    };

    /** @param {MouseEvent} event */
    #onDocumentClick = (event) => {
        if (event.target instanceof Node && this.contains(event.target)) return;

        this.#closeAll();
    };

    /** @param {KeyboardEvent} event */
    #onKeyDown = (event) => {
        if (event.key !== 'Escape') return;

        const openDropdown = this.refs.dropdowns?.find((dropdown) => dropdown.open);
        if (!openDropdown) return;

        this.#closeAll();
        openDropdown.querySelector('summary')?.focus();
    };

    #closeAll() {
        this.refs.dropdowns?.forEach((dropdown) => dropdown.removeAttribute('open'));
    }
}

if (!customElements.get('na-announcement-menu-component')) {
    customElements.define('na-announcement-menu-component', NaAnnouncementMenu);
}
